class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.mainCurrentIndex = 0;
    // Bind methods to ensure 'this' refers to the component instance
    this.updateMainCurrentIndex = this.updateMainCurrentIndex.bind(this);
    this.thumbPrevClick = this.thumbPrevClick.bind(this);
    this.thumbNextClick = this.thumbNextClick.bind(this);

    // For cleanup
    this.thumbSlideClickHandlers = [];
    this.handleResize = this.handleResize.bind(this);
  }

  static get observedAttributes() {
    return ["data-active-variant-handle"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-active-variant-handle") {
      this.onVariantChange(newValue);
      this.checkThumbnailArrows();
    }
  }

  connectedCallback() {
    // Main Media Gallery Elements
    this.mainMediaGallery = this.querySelector(".main-mediaGallery");
    this.mainSlides = Array.from(this.querySelectorAll(".main-slide"));
    if (this.mainMediaGallery) {
      this.mainMediaGallery.addEventListener(
        "scroll",
        this.debounce(this.updateMainCurrentIndex.bind(this), 100)
      );
    }

    // Thumbnail Elements
    this.thumbMediaGallery = this.querySelector(".thumbnail-mediaGallery");
    this.navDots = Array.from(this.querySelectorAll(".nav-dot"));
    this.thumbSlides = Array.from(this.querySelectorAll(".thumbnail-slide"));
    this.thumbPrevBtn = this.querySelector(".thumb-prev-btn");
    this.thumbNextBtn = this.querySelector(".thumb-next-btn");
    this.thumbTrack = this.querySelector(".thumbnail-mediaGallery-track");
    this.galleryContainer = this;

    // Initialize gallery and check arrows
    this.initializeGallery();
    this.checkThumbnailArrows();
    window.addEventListener("resize", this.handleResize);
  }

  disconnectedCallback() {
    this.removeEventListeners();
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize() {
    this.checkThumbnailArrows();
  }

  checkThumbnailArrows() {
    if (!this.galleryContainer || !this.thumbTrack) return;
    requestAnimationFrame(() => {
      if (
        this.thumbTrack.scrollWidth <=
        this.thumbTrack.parentElement.clientWidth + 1
      ) {
        this.galleryContainer.classList.add("no-thumbnail-arrows");
      } else {
        this.galleryContainer.classList.remove("no-thumbnail-arrows");
      }
    });
  }

  onVariantChange(newVariantHandle) {
    this.updateVisibleSlides();
    this.initializeGallery();
    this.checkThumbnailArrows();
  }

  initializeGallery() {
    this.updateVisibleSlides();
    this.removeEventListeners();

    // Nav-dot handlers
    if (this.navDots) {
      this.navDots.forEach((dot, dotIndex) => {
        const handler = () => {
          this.mainCurrentIndex = dotIndex;
          this.scrollToMainSlide(this.mainCurrentIndex);
          this.updateActiveNavDot();
          this.updateActiveThumbnail();
        };
        dot.addEventListener("click", handler);
        // Optional: save in an array for removal on disconnect
        if (!this.dotClickHandlers) this.dotClickHandlers = [];
        this.dotClickHandlers.push({ dot, handler });
      });
    }

    if (this.thumbPrevBtn && this.thumbNextBtn && this.thumbMediaGallery) {
      this.thumbPrevBtn.addEventListener("click", this.thumbPrevClick);
      this.thumbNextBtn.addEventListener("click", this.thumbNextClick);
    }

    if (this.visibleThumbSlides) {
      this.visibleThumbSlides.forEach((thumbSlide, visibleIndex) => {
        const handler = () => {
          this.mainCurrentIndex = visibleIndex;
          this.scrollToMainSlide(this.mainCurrentIndex);
          this.updateActiveThumbnail();
          this.updateActiveNavDot();
        };
        thumbSlide.addEventListener("click", handler);
        this.thumbSlideClickHandlers.push({ thumbSlide, handler });
      });
    }

    this.mainCurrentIndex = 0;
    this.scrollToMainSlide(this.mainCurrentIndex);
    this.updateActiveThumbnail();

    this.checkThumbnailArrows();
    this.updateActiveNavDot();
  }

  removeEventListeners() {
    if (this.mainMediaGallery) {
      this.mainMediaGallery.removeEventListener(
        "scroll",
        this.updateMainCurrentIndex
      );
    }
    if (this.thumbPrevBtn && this.thumbNextBtn && this.thumbMediaGallery) {
      this.thumbPrevBtn.removeEventListener("click", this.thumbPrevClick);
      this.thumbNextBtn.removeEventListener("click", this.thumbNextClick);
    }
    if (this.thumbSlideClickHandlers) {
      this.thumbSlideClickHandlers.forEach(({ thumbSlide, handler }) => {
        thumbSlide.removeEventListener("click", handler);
      });
      this.thumbSlideClickHandlers = [];
    }
    if (this.dotClickHandlers) {
      this.dotClickHandlers.forEach(({ dot, handler }) => {
        dot.removeEventListener("click", handler);
      });
      this.dotClickHandlers = [];
    }
  }

  updateVisibleSlides() {
    this.visibleMainSlides = this.getVisibleSlides(this.mainSlides);
    this.visibleThumbSlides = this.getVisibleSlides(this.thumbSlides);
    this.renderNavDots();
  }

  renderNavDots() {
    const navTrack = this.querySelector(".mediaGallery-nav-track");
    if (!navTrack || !this.visibleMainSlides) return;

    // Remove existing dots
    navTrack.innerHTML = "";

    this.visibleMainSlides.forEach((_, idx) => {
      const dot = document.createElement("button");
      dot.className =
        "nav-dot" + (idx === this.mainCurrentIndex ? " active" : "");
      dot.setAttribute("aria-label", `Slide ${idx + 1}`);
      dot.setAttribute("data-index", idx);
      dot.type = "button";
      navTrack.appendChild(dot);
    });

    // Update navDots cache and handlers
    this.navDots = Array.from(navTrack.querySelectorAll(".nav-dot"));

    // Attach click handlers
    if (this.navDots) {
      if (!this.dotClickHandlers) this.dotClickHandlers = [];
      // Remove old
      this.dotClickHandlers.forEach(({ dot, handler }) =>
        dot.removeEventListener("click", handler)
      );
      this.dotClickHandlers = [];
      // Add new
      this.navDots.forEach((dot, dotIndex) => {
        const handler = () => {
          this.mainCurrentIndex = dotIndex;
          this.scrollToMainSlide(this.mainCurrentIndex);
          this.updateActiveNavDot();
          this.updateActiveThumbnail();
        };
        dot.addEventListener("click", handler);
        this.dotClickHandlers.push({ dot, handler });
      });
    }
  }

  getVisibleSlides(slides) {
    return slides?.filter((slide) => {
      return slide.offsetParent !== null;
    });
  }

  scrollToMainSlide(visibleIndex) {
    if (this.visibleMainSlides === undefined) return;
    const targetSlide = this.visibleMainSlides[visibleIndex];
    if (targetSlide) {
      const scrollLeft =
        targetSlide.offsetLeft - this.mainMediaGallery.offsetLeft;
      this.mainMediaGallery.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
      this.updateActiveThumbnail();
    }
  }

  debounce(func, wait) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  updateMainCurrentIndex() {
    const scrollLeft = this.mainMediaGallery.scrollLeft;
    const slideWidth = this.mainMediaGallery.offsetWidth;
    this.mainCurrentIndex = Math.round(scrollLeft / slideWidth);

    const totalVisibleSlides = this.visibleMainSlides.length;
    this.mainCurrentIndex = Math.max(
      0,
      Math.min(this.mainCurrentIndex, totalVisibleSlides - 1)
    );

    this.updateActiveThumbnail();
    this.updateActiveNavDot();
  }

  thumbPrevClick() {
    this.updateMainCurrentIndex();
    this.mainCurrentIndex =
      this.mainCurrentIndex === 0
        ? this.visibleMainSlides.length - 1
        : this.mainCurrentIndex - 1;

    this.scrollToMainSlide(this.mainCurrentIndex);
    this.adjustThumbnailScroll(this.mainCurrentIndex);
    this.checkThumbnailArrows();
  }

  thumbNextClick() {
    this.updateMainCurrentIndex();
    this.mainCurrentIndex =
      this.mainCurrentIndex === this.visibleMainSlides.length - 1
        ? 0
        : this.mainCurrentIndex + 1;

    this.scrollToMainSlide(this.mainCurrentIndex);
    this.adjustThumbnailScroll(this.mainCurrentIndex);
    this.checkThumbnailArrows();
  }

  adjustThumbnailScroll(visibleIndex) {
    const targetThumbSlide = this.visibleThumbSlides[visibleIndex];
    if (targetThumbSlide) {
      const thumbSlideOffset =
        targetThumbSlide.offsetLeft - this.thumbMediaGallery.offsetLeft;
      const thumbMediaGalleryWidth = this.thumbMediaGallery.offsetWidth;
      const thumbSlideWidth = targetThumbSlide.offsetWidth;

      if (
        thumbSlideOffset < this.thumbMediaGallery.scrollLeft ||
        thumbSlideOffset + thumbSlideWidth >
          this.thumbMediaGallery.scrollLeft + thumbMediaGalleryWidth
      ) {
        const scrollTo =
          thumbSlideOffset - thumbMediaGalleryWidth / 2 + thumbSlideWidth / 2;
        this.thumbMediaGallery.scrollTo({
          left: scrollTo,
          behavior: "smooth",
        });
      }
    }
  }

  updateActiveThumbnail() {
    if (this.visibleThumbSlides === undefined) return;
    this.visibleThumbSlides.forEach((thumbSlide, visibleIndex) => {
      thumbSlide.classList.toggle(
        "active",
        visibleIndex === this.mainCurrentIndex
      );
    });
  }

  updateActiveNavDot() {
    if (!this.navDots) return;
    // Remove 'active' from any previously active dot
    const prevActive = this.navDots.find(dot => dot.classList.contains("active"));
    if (prevActive) prevActive.classList.remove("active");
    // Add 'active' to the current one
    if (this.navDots[this.mainCurrentIndex]) {
      this.navDots[this.mainCurrentIndex].classList.add("active");
    }
  }
}

// Register the component
customElements.define("product-media-gallery", ProductMediaGallery);
